import { CommonModule } from '@angular/common';
import {
	Component,
	computed,
	inject,
	input,
	Signal,
	signal,
} from '@angular/core';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DynamicModule, InputsType } from 'ng-dynamic-component';

import { DropdownComponent } from '@valk-nx/components/ui-dropdown/src/lib/dropdown';
import { InputComponent } from '@valk-nx/components/ui-input/src/lib/input';
import { LabelPosition } from '@valk-nx/components/ui-label/src/lib/label.interface';
import { LabelComponent } from '@valk-nx/components/ui-label/src/lib/label/label';
import { PersuadeCardComponent } from '@valk-nx/compositions/ui-persuade-card/src/lib/persuade-card.component';
import { PersuadeCardInputs } from '@valk-nx/compositions/ui-persuade-card/src/lib/persuade-card.interface';
import { PRICING_TOOLTIP_OCCUPANCY } from '@valk-nx/core/lib/core';
import { HeadingType } from '@valk-nx/core/lib/directives/heading/heading.type';
import { GeneralHelper } from '@valk-nx/helpers/lib/general/general.helper';
import { ColumnsComponent } from '@valk-nx/storyblok/components/columns/src/lib/columns';
import { RichTextComponent } from '@valk-nx/storyblok/components/richtext/src/lib/rich-text';
import { StoryblokDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok.directive';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import {
	Button,
	CardInterface,
	ISbStoryBlok,
} from '@valk-nx/storyblok-types/src/lib/types/storyblok.types';

import { SortOption, SortType } from './searchable-list-section.interface';

const Components = {
	richText: RichTextComponent,
};

@Component({
	selector: 'sb-searchable-list-section',
	templateUrl: './searchable-list-section.html',
	imports: [
		ColumnsComponent,
		CommonModule,
		DropdownComponent,
		DynamicModule,
		InputComponent,
		LabelComponent,
		PersuadeCardComponent,
		StoryblokDirective,
		TranslatePipe,
	],
})
export class StoryblokSearchableListSectionComponent extends StoryblokRootDirective {
	private readonly translate = inject(TranslateService);

	title = input.required<ISbStoryBlok[]>();
	items = input.required<CardInterface[]>();
	useStoryblokCards = input<boolean>(true);

	components: typeof Components = Components;
	persuadeCardItems: Signal<
		(PersuadeCardInputs & { normalizedName: string })[]
	>;

	labelPosition = LabelPosition.TopStacked;
	searchText = signal('');
	sortType = signal<string>('');

	titleInput = computed<(InputsType & { component: string })[]>(() => {
		if (typeof this.title() !== 'string') {
			return (
				this.title()?.map((blok) => ({
					...blok,
					component: blok?.['component'] ?? '',
					maxWidth: 'max-w-screen-xl',
				})) ?? []
			);
		}

		return [];
	});

	sortMap: Record<
		SortType,
		(
			a: { title: string; price: number },
			b: { title: string; price: number },
		) => number
	> = {
		NAME_ASC: (a, b) => a.title.localeCompare(b.title),
		NAME_DESC: (a, b) => b.title.localeCompare(a.title),
		PRICE_ASC: (a, b) => a.price - b.price,
		PRICE_DESC: (a, b) => b.price - a.price,
	};

	sortOptions = computed<SortOption[]>(() => {
		const translate = (label: string) => this.translate.instant(label);
		const pricing = this.useStoryblokCards()
			? []
			: [
					{
						value: 'PRICE_ASC' as SortType,
						label: translate('global.sort.price-asc'),
					},
					{
						value: 'PRICE_DESC' as SortType,
						label: translate('global.sort.price-desc'),
					},
				];

		return [
			{ value: 'NAME_ASC', label: translate('global.sort.name-asc') },
			{ value: 'NAME_DESC', label: translate('global.sort.name-desc') },
			...pricing,
		];
	});

	orderedList = computed(() => {
		return this.persuadeCardItems()
			.filter((item) =>
				item.normalizedName.toLowerCase().includes(this.searchText()),
			)
			.sort(this.sortMap[this.sortType()]);
	});

	pricingTooltipOccupancy = PRICING_TOOLTIP_OCCUPANCY;

	constructor() {
		super();

		this.persuadeCardItems = computed(() =>
			// TODO: VPL-36499 use data from api when available with check on useStoryblokCards
			this.items().map((item) => {
				const title = item.heading[0]?.title as string;
				return {
					content: item.content as unknown as string,
					ctaText: this.mapButton(item.footer[0])?.ctaText,
					ctaUrl: this.mapButton(item.footer[0])?.ctaUrl,
					headingType: item.heading[0]?.headingType as HeadingType,
					images: item.images.map((image) => ({
						src: image.bynder[0].files.transformBaseUrl.url,
						alt: image.imageAlt,
					})),
					title,
					normalizedName: GeneralHelper.getNormalizedName(title),
				};
			}),
		);
	}

	mapButton(cardFooter: Button) {
		if (cardFooter?.component === 'button') {
			return {
				ctaText: cardFooter.text,
				ctaUrl: cardFooter.link?.cached_url,
			};
		}
		return null;
	}

	sort(sortType: string): void {
		this.sortType.set(sortType);
	}

	setSearchText(value: unknown) {
		if (typeof value === 'string') {
			this.searchText.set(
				GeneralHelper.getNormalizedName(value).toLowerCase(),
			);
		}
	}
}
